import apiService from './api';

// User management types
export interface PendingUser {
  UserId: string;
  Email: string;
  FirstName?: string;
  LastName?: string;
  CreatedAt: number;
  Status: 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED';
}

export interface ApprovalResponse {
  success: boolean;
  userId: string;
}

export interface PendingUsersResponse {
  users: PendingUser[];
  nextToken?: string;
}

/**
 * User Management Service
 * Provides admin functionality for managing user accounts
 */
const userManagementService = {
  /**
   * Get list of pending users
   * @param limit Optional number of users to retrieve
   * @param nextToken Optional pagination token
   */
  getPendingUsers: async (limit?: number, nextToken?: string): Promise<PendingUsersResponse> => {
    const params = { limit, nextToken };
    return apiService.get<PendingUsersResponse>('/admin/users/pending', { params });
  },

  /**
   * Get all users
   * @param limit Optional number of users to retrieve
   * @param nextToken Optional pagination token
   */
  getAllUsers: async (limit?: number, nextToken?: string): Promise<PendingUsersResponse> => {
    const params = { limit, nextToken };
    return apiService.get<PendingUsersResponse>('/admin/users', { params });
  },

  /**
   * Approve a user
   * @param userId ID of the user to approve
   */
  approveUser: async (userId: string): Promise<ApprovalResponse> => {
    return apiService.post<ApprovalResponse>('/admin/users/approve', { userId });
  },

  /**
   * Reject a user
   * @param userId ID of the user to reject
   * @param reason Optional reason for rejection
   */
  rejectUser: async (userId: string, reason?: string): Promise<ApprovalResponse> => {
    return apiService.post<ApprovalResponse>('/admin/users/reject', { userId, reason });
  },

  /**
   * Get user details by ID
   * @param userId ID of the user to retrieve
   */
  getUserById: async (userId: string): Promise<PendingUser> => {
    return apiService.get<PendingUser>(`/admin/users/${userId}`);
  }
};

export default userManagementService;